import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  loginWithInitData,
  loginDev,
  getBalance,
  type AuthUser,
  ApiError,
} from '../api/client'
import { getInitData } from './telegram'
import i18n from '../i18n'

const TOKEN_KEY = 'tg_game_jwt'

/** Survives React StrictMode remount after ?token= is stripped from the URL. */
let handoffMemory: string | null = null

function readStoredToken(): string | null {
  if (handoffMemory) return handoffMemory
  try {
    return sessionStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

function writeStoredToken(t: string): void {
  handoffMemory = t
  try {
    sessionStorage.setItem(TOKEN_KEY, t)
  } catch {
    /* private mode */
  }
}

function clearStoredToken(): void {
  handoffMemory = null
  try {
    sessionStorage.removeItem(TOKEN_KEY)
  } catch {
    /* ignore */
  }
}

/** Pull ?token= from URL once; always persist before strip (StrictMode-safe). */
function takeTokenFromUrl(): string | null {
  try {
    const u = new URL(window.location.href)
    const t = u.searchParams.get('token')
    if (!t) return null
    writeStoredToken(t)
    u.searchParams.delete('token')
    const qs = u.searchParams.toString()
    window.history.replaceState(
      null,
      '',
      u.pathname + (qs ? `?${qs}` : '') + u.hash,
    )
    return t
  } catch {
    return null
  }
}

function placeholderUser(balance: number): AuthUser {
  return {
    id: 0,
    telegram_id: 0,
    username: 'player',
    first_name: 'Player',
    last_name: '',
    balance,
    language_code: i18n.language?.startsWith('en') ? 'en' : 'zh-TW',
  }
}

type AuthState = {
  status: 'loading' | 'ready' | 'error'
  token: string | null
  user: AuthUser | null
  error: string | null
  refreshBalance: () => Promise<void>
  retry: () => void
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthState['status']>('loading')
  const [token, setToken] = useState<string | null>(null)
  const [user, setUser] = useState<AuthUser | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  // Intentionally depend only on `tick` so language changes do not re-auth in a loop.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      setStatus('loading')
      setError(null)
      try {
        // 1) Game → lobby JWT handoff (URL or memory/session).
        takeTokenFromUrl()
        const handed = readStoredToken()
        if (handed) {
          try {
            const { balance } = await getBalance(handed)
            if (cancelled) return
            writeStoredToken(handed)
            setToken(handed)
            setUser(placeholderUser(balance))
            setStatus('ready')
            // Best-effort: upgrade to Telegram profile when still inside TMA.
            void (async () => {
              try {
                const initData = await getInitData()
                if (!initData || cancelled) return
                const res = await loginWithInitData(initData)
                if (cancelled) return
                writeStoredToken(res.token)
                setToken(res.token)
                setUser(res.user)
              } catch {
                /* keep handoff session */
              }
            })()
            return
          } catch {
            clearStoredToken()
            // fall through to normal login
          }
        }

        // 2) Telegram Mini App initData.
        const initData = await getInitData()
        if (initData) {
          const res = await loginWithInitData(initData)
          if (cancelled) return
          writeStoredToken(res.token)
          setToken(res.token)
          setUser(res.user)
          const want = res.user.language_code?.startsWith('en') ? 'en' : 'zh-TW'
          if (i18n.language !== want && !i18n.language?.startsWith(want)) {
            void i18n.changeLanguage(want)
          }
          setStatus('ready')
          return
        }

        // 3) Local debug only (Gateway mode != release).
        const res = await loginDev({ username: 'local_dev', first_name: 'Local' })
        if (cancelled) return
        writeStoredToken(res.token)
        setToken(res.token)
        setUser(res.user)
        setStatus('ready')
      } catch (err: unknown) {
        if (cancelled) return
        let msg = i18n.t('auth.failed')
        if (err instanceof ApiError) {
          msg = err.code === 'not_found'
            ? i18n.t('auth.notFound')
            : i18n.t(`errors.${err.code}`, { defaultValue: i18n.t('auth.failed') })
        }
        setStatus('error')
        setError(msg || i18n.t('auth.failed'))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [tick])

  const refreshBalance = useCallback(async () => {
    if (!token) return
    try {
      const { balance } = await getBalance(token)
      setUser((u) => (u ? { ...u, balance } : u))
    } catch {
      /* keep previous balance */
    }
  }, [token])

  const value = useMemo<AuthState>(
    () => ({
      status,
      token,
      user,
      error,
      refreshBalance,
      retry: () => setTick((n) => n + 1),
    }),
    [status, token, user, error, refreshBalance],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
